import { settingFieldType } from 'config/settingFieldType';
import describeApiError from 'library/helpers/describeApiError';
import { JSON_FIELD_TYPES, mergedSettings, parseSettingValue } from 'library/helpers/settingsHelper';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { settingsActions } from './slice';
import { Notification } from 'zComponents/atoms';

const selectedProfile = (state) => state.Auth.selectedProfile;

const SETTINGS_CACHE_TTL = 5 * 60 * 1000;

export function* loadOrgSettings(action) {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;
	try {
		const { orgID } = yield select(selectedProfile);

		if (!orgID) {
			throw new Error('No orgID found in the current profile');
		}

		// SWR: skip if this org's settings were fetched recently, unless the caller
		// explicitly requests a refresh (e.g. after an upsert).
		const force = action?.payload?.force;
		if (!force) {
			const ts = yield select((state) => state.Settings.orgSettingsTimeStamp?.[orgID]);
			if (ts && Date.now() - ts < SETTINGS_CACHE_TTL) return;
		}

		const orgSettings = yield superFetch.get(`/settings`, {}, {}, orgID).then((res) => {
			if (res.statusCode === 200) {
				return res.data;
			} else {
				throw new Error(describeApiError(res));
			}
		});

		yield put(settingsActions.loadOrgSettingsSuccess({ settings: orgSettings, orgID: orgID }));
	} catch (error) {
		yield put(settingsActions.loadOrgSettingsError(error.message));
	}
}

export function* loadOrgSettingsSuccess({ payload }) {
	// merge the default settings with the org settings
	const { settings, orgID } = payload;

	const defaultSettings = yield select((state) => state.DefaultSettings.orgDefaultSettings);
	const res = mergedSettings(defaultSettings, settings);

	yield put(settingsActions.mergeOrgSettings({ orgID, mergedSettings: res }));
}

export function* loadPersonSettings(action) {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;
	try {
		const { orgID, personID } = yield select(selectedProfile);

		if (!orgID || !personID) {
			throw new Error('No orgID or personID found in the current profile');
		}

		const force = action?.payload?.force;
		if (!force) {
			const ts = yield select((state) => state.Settings.personSettingsTimeStamp?.[personID]);
			if (ts && Date.now() - ts < SETTINGS_CACHE_TTL) return;
		}

		const personSettings = yield superFetch.get(`/settings/people`, {}, { personID }, orgID).then((res) => {
			if (res.statusCode === 200) {
				return res.data;
			} else {
				throw new Error(describeApiError(res));
			}
		});

		yield put(settingsActions.loadPersonSettingsSuccess({ settings: personSettings, personID: personID }));
	} catch (error) {
		yield put(settingsActions.loadPersonSettingsError(error.message));
	}
}

export function* loadPersonSettingsSuccess({ payload }) {
	// merge the default settings with the person settings
	const { settings, personID } = payload;

	const defaultSettings = yield select((state) => state.DefaultSettings.personDefaultSettings);
	const res = mergedSettings(defaultSettings, settings);

	yield put(settingsActions.mergePersonSettings({ personID, mergedSettings: res }));
}

export function* updatePersonSetting({ payload }) {
	try {
		const superFetchModule = yield import('library/helpers/superFetch');
		const superFetch = superFetchModule.default;
		const { personID } = yield select(selectedProfile);

		const body = {
			defaultSettingID: payload.defaultSettingID,
			value: [settingFieldType.MULTISELECT, settingFieldType.MULTIVALUE].includes(payload.fieldTypeID)
				? JSON.stringify(payload.value)
				: payload.value,
			personID: personID
		};

		const res = yield superFetch.post('/settings/people', body);

		if (res.statusCode === 200) {
			yield put(settingsActions.loadPersonSettings({ force: true }));
		} else {
			throw new Error(describeApiError(res));
		}
	} catch (error) {
		yield put(settingsActions.updatePersonSettingError(error.message));
	}
}

// JSON types are stored stringified; '' is sent as-is so the row falls back to its default.
const encodeValue = (setting) => {
	const jsonEncoded = JSON_FIELD_TYPES.includes(setting.fieldTypeID);
	return jsonEncoded && setting.value !== '' ? JSON.stringify(setting.value) : setting.value;
};

// What the row shows while its save is in flight: '' (reset) shows the default.
const optimisticValue = (setting) =>
	setting.value === '' ? (parseSettingValue(setting.fieldTypeID, setting.defaultValue) ?? '') : setting.value;

export function* upsertOrgSettings(action) {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;

	const { settings } = action.payload || {};
	const defaultSettingIDs = (settings || []).map((s) => s.defaultSettingID);

	if (!Array.isArray(settings) || settings.length === 0) {
		yield put(settingsActions.upsertOrgSettingsSuccess({ defaultSettingIDs }));
		return;
	}

	const { orgID } = yield select(selectedProfile);
	for (const setting of settings) {
		yield put(
			settingsActions.updateOrgSettingValue({
				orgID,
				defaultSettingID: setting.defaultSettingID,
				value: optimisticValue(setting)
			})
		);
	}

	try {
		const responses = yield all(
			settings.map((setting) =>
				call(
					superFetch.post,
					'/settings',
					{
						defaultSettingID: setting.defaultSettingID,
						value: encodeValue(setting)
					},
					{}
				)
			)
		);

		// superFetch resolves with { statusCode, error } instead of throwing.
		const failed = (responses || []).find((res) => res?.statusCode !== 200);
		if (failed) throw new Error(failed.error?.message || 'Failed to save settings');

		yield put(settingsActions.upsertOrgSettingsSuccess({ defaultSettingIDs }));
		yield put(settingsActions.loadOrgSettings({ force: true }));

		Notification('success', 'Settings were saved successfully.');
	} catch (error) {
		console.error('upsertOrgSettings failed:', error);
		yield put(
			settingsActions.upsertOrgSettingsError({
				error: error?.message || 'Failed to save settings',
				defaultSettingIDs
			})
		);
		// Reload rolls the optimistic values back to what the server holds.
		yield put(settingsActions.loadOrgSettings({ force: true }));
		Notification('error', 'Failed to save settings. Please try again.');
	}
}

// The main export for the saga
export default function* settingsSagas() {
	yield takeLatest(settingsActions.loadOrgSettings, loadOrgSettings);
	yield takeLatest(settingsActions.loadPersonSettings, loadPersonSettings);
	yield takeLatest(settingsActions.loadOrgSettingsSuccess, loadOrgSettingsSuccess);
	yield takeLatest(settingsActions.loadPersonSettingsSuccess, loadPersonSettingsSuccess);
	yield takeLatest(settingsActions.updatePersonSetting, updatePersonSetting);
	yield takeEvery(settingsActions.upsertOrgSettings, upsertOrgSettings);
}
